import { useState, type ReactNode } from 'react';
import type { VacationRequest } from '@domain/entities/VacationRequest';
import { getServices } from '@infrastructure/services';
import { DocumentViewer, descargarDocumento } from '../../documents/DocumentViewer';
import { nombreDescarga } from '../../documents/nombreDescarga';
import { useToast } from '../../providers/ToastProvider';
import styles from './RequestDocument.module.css';

/**
 * Ver y descargar el registro firmado, desde la tarjeta del tablero.
 *
 * Antes la tarjeta sólo decía «PDF adjunto», que responde a la pregunta
 * equivocada: el líder no quiere saber que existe, quiere leerlo antes de
 * firmar. Catorce de las diecisiete solicitudes de producción traen uno.
 *
 * La URL se pide al abrir, no al pintar la tarjeta. Son URLs con token y de
 * vida corta, y resolverlas para cada tarjeta del tablero sería una llamada a
 * Storage por tarjeta cada vez que el tablero se refresca —que ahora es a
 * menudo, porque escucha en vivo— para enlaces que casi nadie va a abrir.
 *
 * El visor vivía aquí dentro y ahora es de todos: `documents/DocumentViewer`.
 * Lo mismo que sirve para el registro firmado sirve para el expediente y para
 * el certificado del IMSS, y una sola caja significa que arreglar el recorte de
 * una esquina lo arregla en los tres sitios.
 */

interface Props {
  readonly request: VacationRequest;
}

export const RequestDocumentActions = ({ request }: Props): ReactNode => {
  const [viewing, setViewing] = useState(false);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (request.pdfPath === null) return null;
  const path = request.pdfPath;
  const archivo = nombreDescarga('Registro', request.folio, request.personName);

  const download = async (): Promise<void> => {
    setBusy(true);
    const url = await getServices().documents.resolveDownloadUrl(path);
    setBusy(false);

    if (!url.ok) {
      toast.bad('No se pudo abrir el registro firmado.');
      return;
    }
    await descargarDocumento(url.value, archivo);
  };

  return (
    <>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.view}
          onClick={() => {
            setViewing(true);
          }}
        >
          <span aria-hidden="true">▤</span> Ver registro
        </button>
        <button
          type="button"
          className={styles.download}
          disabled={busy}
          aria-label={`Descargar el registro de ${request.folio}`}
          onClick={() => {
            void download();
          }}
        >
          <span aria-hidden="true">↓</span>
        </button>
      </div>

      {viewing && (
        <DocumentViewer
          documentos={[
            { titulo: `Registro de ${request.folio}`, path, nombreArchivo: archivo },
          ]}
          subtitulo={request.personName}
          onClose={() => {
            setViewing(false);
          }}
        />
      )}
    </>
  );
};
